import { useCallback, useEffect, useMemo, useRef } from "react";
import { TaskTemplateAssigneeRole } from "@common/types/taskTemplate";
import { OrganizationCloseStage } from "@common/types/organizationCloseStage";
import {
    CommandsDirectory,
    InjectedCommandOption,
} from "@components/commands/lib";
import { useCommands } from "@components/commands/useCommands";
import {
    defaultDurationPresets,
    formatDuration,
} from "@design-system/TimePicker/TimePicker.lib";
import {
    ASSIGNEE_ROLE_LABEL,
    ASSIGNEE_ROLE_OPTIONS,
    formatDeadlineLabel,
    getDeadlineOptions,
} from "./closeTemplateTasksDrawer.constants";
import { LocalTask } from "./closeTemplateTasksDrawer.types";
import { deleteTasksWithLinks } from "./closeTemplateTaskLinks";

interface UseCloseTemplateTaskBulkActionsParams {
    selectedLocalIds: string[];
    localTasks: LocalTask[];
    onLocalTasksChange: (updater: (prev: LocalTask[]) => LocalTask[]) => void;
    clearSelection: () => void;
    sortedStages: OrganizationCloseStage[];
}

export function useCloseTemplateTaskBulkActions({
    selectedLocalIds,
    localTasks,
    onLocalTasksChange,
    clearSelection,
    sortedStages,
}: UseCloseTemplateTaskBulkActionsParams) {
    const {
        open: openCommands,
        registerOpenOverride,
        unregisterOpenOverride,
    } = useCommands();

    // The command menu snapshots its options (including these onSelect
    // callbacks) at open time, so read the live selection from a ref to avoid
    // acting on a stale selection if it changes while the menu is open.
    const selectedLocalIdsRef = useRef(selectedLocalIds);
    selectedLocalIdsRef.current = selectedLocalIds;

    const handleBulkUpdateDeadline = useCallback(
        (externalDeadline: number | null) => {
            const ids = new Set(selectedLocalIdsRef.current);
            onLocalTasksChange((prev) =>
                prev.map((task) =>
                    ids.has(task.localId)
                        ? { ...task, externalDeadline }
                        : task,
                ),
            );
            clearSelection();
        },
        [onLocalTasksChange, clearSelection],
    );

    const handleBulkUpdateRole = useCallback(
        (assigneeRole: TaskTemplateAssigneeRole | null) => {
            const ids = new Set(selectedLocalIdsRef.current);
            onLocalTasksChange((prev) =>
                prev.map((task) =>
                    ids.has(task.localId) ? { ...task, assigneeRole } : task,
                ),
            );
            clearSelection();
        },
        [onLocalTasksChange, clearSelection],
    );

    const handleBulkUpdateStage = useCallback(
        (closeStageId: string | null) => {
            const ids = new Set(selectedLocalIdsRef.current);
            onLocalTasksChange((prev) =>
                prev.map((task) =>
                    ids.has(task.localId) ? { ...task, closeStageId } : task,
                ),
            );
            clearSelection();
        },
        [onLocalTasksChange, clearSelection],
    );

    const handleBulkUpdateEstimate = useCallback(
        (estimatedTime: number | null) => {
            const ids = new Set(selectedLocalIdsRef.current);
            onLocalTasksChange((prev) =>
                prev.map((task) =>
                    ids.has(task.localId) ? { ...task, estimatedTime } : task,
                ),
            );
            clearSelection();
        },
        [onLocalTasksChange, clearSelection],
    );

    const handleBulkDelete = useCallback(() => {
        const ids = new Set(selectedLocalIdsRef.current);
        onLocalTasksChange((prev) => deleteTasksWithLinks(prev, ids));
        clearSelection();
    }, [onLocalTasksChange, clearSelection]);

    // Gather all deadline values currently in use by selected tasks to include
    // legacy values (>30 days) in the dropdown.
    const selectedDeadlineValues = useMemo(() => {
        const ids = new Set(selectedLocalIds);
        return localTasks.flatMap((t) =>
            ids.has(t.localId) && t.externalDeadline !== null
                ? [t.externalDeadline]
                : [],
        );
    }, [selectedLocalIds, localTasks]);

    const deadlineOptions = useMemo((): InjectedCommandOption[] => {
        const allValues = selectedDeadlineValues.reduce(
            (acc, v) => {
                // getDeadlineOptions handles legacy values
                const opts = getDeadlineOptions(v);
                opts.forEach((o) => acc.add(o));
                return acc;
            },
            new Set(getDeadlineOptions(null)),
        );
        const sorted = Array.from(allValues).sort((a, b) => a - b);

        const noneOption: InjectedCommandOption = {
            id: "close-template-deadline-none",
            label: "None",
            keywords: ["none", "clear", "remove deadline"],
            onSelect: () => handleBulkUpdateDeadline(null),
        };

        const options: InjectedCommandOption[] = sorted.map((days) => ({
            id: `close-template-deadline-${days}`,
            label: formatDeadlineLabel(days),
            keywords: [String(days), "days"],
            onSelect: () => handleBulkUpdateDeadline(days),
        }));

        return [noneOption, ...options];
    }, [selectedDeadlineValues, handleBulkUpdateDeadline]);

    const roleOptions = useMemo((): InjectedCommandOption[] => {
        const noRoleOption: InjectedCommandOption = {
            id: "close-template-role-none",
            label: "No role",
            keywords: ["none", "clear", "remove role", "unassign"],
            onSelect: () => handleBulkUpdateRole(null),
        };

        const options: InjectedCommandOption[] = ASSIGNEE_ROLE_OPTIONS.map(
            (role) => ({
                id: `close-template-role-${role}`,
                label: ASSIGNEE_ROLE_LABEL[role],
                keywords: [role, ASSIGNEE_ROLE_LABEL[role].toLowerCase()],
                onSelect: () => handleBulkUpdateRole(role),
            }),
        );

        return [noRoleOption, ...options];
    }, [handleBulkUpdateRole]);

    const stageOptions = useMemo((): InjectedCommandOption[] => {
        const noStageOption: InjectedCommandOption = {
            id: "close-template-stage-none",
            label: "No stage",
            keywords: ["none", "clear", "remove stage"],
            onSelect: () => handleBulkUpdateStage(null),
        };

        const options: InjectedCommandOption[] = sortedStages.map((stage) => ({
            id: `close-template-stage-${stage.id}`,
            label: stage.name,
            keywords: [stage.name.toLowerCase()],
            onSelect: () => handleBulkUpdateStage(stage.id),
        }));

        return [noStageOption, ...options];
    }, [sortedStages, handleBulkUpdateStage]);

    const estimateOptions = useMemo((): InjectedCommandOption[] => {
        const noneOption: InjectedCommandOption = {
            id: "close-template-estimate-none",
            label: "None",
            keywords: ["none", "clear", "remove estimate"],
            onSelect: () => handleBulkUpdateEstimate(null),
        };

        const options: InjectedCommandOption[] = defaultDurationPresets.map(
            (minutes) => ({
                id: `close-template-estimate-${minutes}`,
                label: formatDuration(minutes),
                keywords: [String(minutes), formatDuration(minutes)],
                onSelect: () => handleBulkUpdateEstimate(minutes),
            }),
        );

        return [noneOption, ...options];
    }, [handleBulkUpdateEstimate]);

    const buildCommandOptions = useCallback(
        () => ({
            selectedCloseTemplateTaskCount: selectedLocalIds.length,
            closeTemplateTaskBulkDeadlineOptions: deadlineOptions,
            closeTemplateTaskBulkRoleOptions: roleOptions,
            closeTemplateTaskBulkStageOptions: stageOptions,
            closeTemplateTaskBulkEstimateOptions: estimateOptions,
            onCloseTemplateTaskBulkDelete: handleBulkDelete,
        }),
        [
            selectedLocalIds.length,
            deadlineOptions,
            roleOptions,
            stageOptions,
            estimateOptions,
            handleBulkDelete,
        ],
    );

    const handleOpenBulkDeadline = useCallback(() => {
        if (!selectedLocalIds.length) {
            return;
        }
        openCommands(
            CommandsDirectory.CLOSE_TEMPLATE_TASK_BULK_DEADLINE,
            buildCommandOptions(),
        );
    }, [openCommands, selectedLocalIds.length, buildCommandOptions]);

    const handleOpenBulkRole = useCallback(() => {
        if (!selectedLocalIds.length) {
            return;
        }
        openCommands(
            CommandsDirectory.CLOSE_TEMPLATE_TASK_BULK_ROLE,
            buildCommandOptions(),
        );
    }, [openCommands, selectedLocalIds.length, buildCommandOptions]);

    const handleOpenBulkStage = useCallback(() => {
        if (!selectedLocalIds.length) {
            return;
        }
        openCommands(
            CommandsDirectory.CLOSE_TEMPLATE_TASK_BULK_STAGE,
            buildCommandOptions(),
        );
    }, [openCommands, selectedLocalIds.length, buildCommandOptions]);

    const handleOpenBulkEstimate = useCallback(() => {
        if (!selectedLocalIds.length) {
            return;
        }
        openCommands(
            CommandsDirectory.CLOSE_TEMPLATE_TASK_BULK_ESTIMATE,
            buildCommandOptions(),
        );
    }, [openCommands, selectedLocalIds.length, buildCommandOptions]);

    const handleOpenBulkActions = useCallback(() => {
        if (!selectedLocalIds.length) {
            return;
        }
        openCommands(
            CommandsDirectory.CLOSE_TEMPLATE_TASK_BULK,
            buildCommandOptions(),
        );
    }, [openCommands, selectedLocalIds.length, buildCommandOptions]);

    const handleOpenBulkActionsRef = useRef(handleOpenBulkActions);
    handleOpenBulkActionsRef.current = handleOpenBulkActions;

    useEffect(() => {
        if (selectedLocalIds.length > 0) {
            registerOpenOverride(() => handleOpenBulkActionsRef.current());
            return () => unregisterOpenOverride();
        }
        unregisterOpenOverride();
    }, [selectedLocalIds.length, registerOpenOverride, unregisterOpenOverride]);

    return {
        handleOpenBulkDeadline,
        handleOpenBulkRole,
        handleOpenBulkStage,
        handleOpenBulkEstimate,
        handleOpenBulkActions,
    };
}
